type FeeTemplate = {
  feetemplate_id: string;
  asset: string;
  asset_type: string;
  type: string;
  fixed: string;
  percentage: string;
  min_amount: string;
  max_amount: string;
  is_active: boolean;
  decimal: number;
  network_display?: string;
};

const getAssetDisplayName = (template: FeeTemplate): string => {
  if (template.network_display && template.network_display.trim() !== "") {
    return `${template.asset}-${template.network_display}`;
  }
  return template.asset;
};

type Props = {
  title: string;
  templates: FeeTemplate[];
  isOpen: boolean;
  onToggle: () => void;
};

const FeeTemplateSectionReadOnly = ({
  title,
  templates,
  isOpen,
  onToggle,
}: Props) => {
  if (templates.length === 0) {
    return null;
  }

  const fiatTemplates = templates.filter((t) => t.asset_type === "fiat");
  const cryptoTemplates = templates.filter((t) => t.asset_type !== "fiat");

  return (
    <div className="mb-4 rounded-xl border border-gray-200 p-6">
      <button
        className="flex w-full items-center justify-between text-base font-semibold capitalize text-navy-700"
        onClick={onToggle}
      >
        <span>{title}</span>
        <svg
          className={`h-4 w-4 transform transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="mt-4">
          {/* Fiat Section */}
          {fiatTemplates.length > 0 && (
            <div className="mb-6">
              <h4 className="mb-2 text-sm font-semibold text-navy-700">Fiat</h4>
              <div className="overflow-x-auto">
                <div className="w-full">
                  {/* Header Row */}
                  <div
                    className="MuiDataGrid-columnHeaders"
                    style={{ minHeight: 48, maxHeight: 48, lineHeight: "48px" }}
                  >
                    <div
                      className="MuiDataGrid-columnHeaderRow flex"
                      style={{ height: 48 }}
                    >
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "28%",
                          minWidth: "28%",
                          padding: "8px 16px",
                          borderTopLeftRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Asset
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "18%",
                          minWidth: "18%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">Min</div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "18%",
                          minWidth: "18%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">Max</div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "18%",
                          minWidth: "18%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Percentage
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "18%",
                          minWidth: "18%",
                          padding: "8px 16px",
                          borderTopRightRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Fixed
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Body Rows */}
                  {fiatTemplates.map((template) => (
                    <div
                      key={template.feetemplate_id}
                      className="MuiDataGrid-row group flex"
                      style={{
                        minHeight: 48,
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "28%" }}
                      >
                        {getAssetDisplayName(template)}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "18%" }}
                      >
                        {template.min_amount || "0"}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "18%" }}
                      >
                        {template.max_amount || "0"}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "18%" }}
                      >
                        {template.percentage || "0"}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "18%" }}
                      >
                        {template.fixed || "0"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Crypto Section */}
          {cryptoTemplates.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-navy-700">
                Crypto
              </h4>
              <div className="overflow-x-auto">
                <div className="w-full">
                  {/* Header Row */}
                  <div
                    className="MuiDataGrid-columnHeaders"
                    style={{ minHeight: 48, maxHeight: 48, lineHeight: "48px" }}
                  >
                    <div
                      className="MuiDataGrid-columnHeaderRow flex"
                      style={{ height: 48 }}
                    >
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "20%",
                          minWidth: "20%",
                          padding: "8px 16px",
                          borderTopLeftRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Asset
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "16%",
                          minWidth: "16%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Asset Type
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "16%",
                          minWidth: "16%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">Min</div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "16%",
                          minWidth: "16%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">Max</div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "16%",
                          minWidth: "16%",
                          padding: "8px 16px",
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Percentage
                        </div>
                      </div>
                      <div
                        className="MuiDataGrid-columnHeader flex items-center"
                        style={{
                          width: "16%",
                          minWidth: "16%",
                          padding: "8px 16px",
                          borderTopRightRadius: 5,
                        }}
                      >
                        <div className="MuiDataGrid-columnHeaderTitle">
                          Fixed
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Body Rows */}
                  {cryptoTemplates.map((template) => (
                    <div
                      key={template.feetemplate_id}
                      className="MuiDataGrid-row group flex"
                      style={{
                        minHeight: 48,
                        borderBottom: "1px solid rgba(224, 224, 224, 1)",
                      }}
                    >
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "20%" }}
                      >
                        {getAssetDisplayName(template)}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "16%" }}
                      >
                        {template.asset_type}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "16%" }}
                      >
                        {template.min_amount || "0"}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "16%" }}
                      >
                        {template.max_amount || "0"}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "16%" }}
                      >
                        {template.percentage || "0"}
                      </div>
                      <div
                        className="MuiDataGrid-cell flex items-center"
                        style={{ width: "16%" }}
                      >
                        {template.fixed || "0"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeeTemplateSectionReadOnly;
